import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import WarningAmber from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import { SxProps } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { RutterExportState } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';

import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { RutterSettlementExport } from '~hooks/useSettlements/models';
import { getRutterExportErrorMessage } from '~utils/settlements/rutter-export-error-utils';

const getRutterExportInfo = (rutterExport: RutterSettlementExport) => {
  switch (rutterExport.state) {
    case RutterExportState.PENDING:
      return {
        label: t(
          'approvals.driver_pay.settlements.rutter_settlement_export.states.pending',
        ),
        icon: (
          <LoadingSpinner isVisible loadingIndicatorSize={16} sx={{ mr: 1, flex: 0 }} />
        ),
      };
    case RutterExportState.EXPORTED:
      return {
        label: t(
          'approvals.driver_pay.settlements.rutter_settlement_export.states.exported',
        ),
        icon: <CheckCircleOutline color="success" sx={{ mr: 1 }} fontSize="small" />,
      };
    case RutterExportState.FAILED:
      return {
        label: t(
          'approvals.driver_pay.settlements.rutter_settlement_export.states.failed',
        ),
        icon: <WarningAmber color="warning" sx={{ mr: 1 }} fontSize="small" />,
      };
    case RutterExportState.NOT_EXPORTED:
    default:
      return {
        label: t(
          'approvals.driver_pay.settlements.rutter_settlement_export.states.not_exported',
        ),
        icon: <></>,
      };
  }
};

interface RutterSettlementExportStatusProps {
  rutterSettlementExport: RutterSettlementExport;
  sx?: SxProps;
  labelSx?: SxProps;
}

const RutterSettlementExportStatus = ({
  rutterSettlementExport,
  sx,
  labelSx,
}: RutterSettlementExportStatusProps) => {
  const { icon, label } = getRutterExportInfo(rutterSettlementExport);

  // Don't show anything if not exported yet
  if (rutterSettlementExport.state === RutterExportState.NOT_EXPORTED) {
    return null;
  }

  const showDate =
    rutterSettlementExport.state === RutterExportState.EXPORTED &&
    rutterSettlementExport.exportedAt;

  // the backend records why an export failed (an unmapped tax rate schedule, a missing
  // item mapping, a QuickBooks rejection); surfacing it here tells the biller what to fix
  const failureReason =
    rutterSettlementExport.state === RutterExportState.FAILED &&
    rutterSettlementExport.lastError
      ? getRutterExportErrorMessage(rutterSettlementExport.lastError)
      : null;

  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {icon}
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Typography variant="body2" sx={labelSx}>
          {label}
          {showDate && (
            <>
              {' • '}
              {dayjs.tz(rutterSettlementExport.exportedAt).format('MMM DD')}
            </>
          )}
        </Typography>
        {failureReason && (
          <Tooltip title={failureReason}>
            <Typography
              data-test-id="rutter-settlement-export-error"
              noWrap
              sx={{ color: 'text.secondary' }}
              tabIndex={0}
              variant="caption"
            >
              {failureReason}
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default RutterSettlementExportStatus;
